import React, { useState, useEffect } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../../api/client';
import { invalidateTickets } from '../../lib/tickets-invalidation';
import { unwrap } from '../../lib/api-envelope';
import { Mail, Loader2, X } from 'lucide-react';
import { SuppressedRecipientNotice, suppressedRefusalOf, type SuppressedRefusal } from '../SuppressedRecipientNotice';

interface Ticket {
  id: string;
  customer: string;
  [key: string]: any;
}

interface CustomerEmailPromptModalProps {
  open: boolean;
  onClose: () => void;
  ticket: Ticket;
  onSent: (updatedTicket: any) => void;
  onToast?: (msg: string, type?: 'success' | 'error') => void;
  initialEmail?: string;
  title?: string;
  description?: string;
}

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function CustomerEmailPromptModal({
  open,
  onClose,
  ticket,
  onSent,
  onToast,
  initialEmail,
  title,
  description,
}: CustomerEmailPromptModalProps) {
  const queryClient = useQueryClient();
  const [email, setEmail] = useState(initialEmail || '');
  const [saveToCustomer, setSaveToCustomer] = useState(true);
  /**
   * The 409 `recipient_suppressed` from this door, held here rather than lifted
   * to the screen because this is the door WITH the address field: the office
   * is looking at the very input the refusal is about, and "send anyway" has to
   * re-post that same value.
   *
   * This is the door the office reaches for after a bounce, so it is the one
   * where the missing `force` actually stranded them. Re-typing the address
   * that just bounced returned a 409 into a vanishing toast, with no way past —
   * for any role. The API has accepted `force` here since the guard landed.
   */
  const [suppressed, setSuppressed] = useState<SuppressedRefusal | null>(null);

  // Sync initialEmail when modal opens or prop changes
  useEffect(() => {
    if (open && initialEmail) setEmail(initialEmail);
  }, [open, initialEmail]);

  const isValidEmail = EMAIL_REGEX.test(email);

  const sendMutation = useMutation({
    mutationFn: (opts?: { force?: boolean }) =>
      api.tickets.sendCustomerEmailWithAddress(ticket.id, {
        email,
        // A forced send must not also overwrite the customer record. The office
        // is asserting this mailbox was repaired under this spelling, not
        // nominating it as the company's address.
        saveToCustomerRecord: opts?.force ? false : saveToCustomer,
        ...(opts?.force ? { force: true } : {}),
      }),
    onSuccess: (response) => {
      setSuppressed(null);
      // POST /tickets/:id/send-customer-email-with-address answers
      // { success, sentTo, savedToCustomer, data: <re-read ticket> }, and that
      // re-read is null if the row vanished. The old `?.data || response.data`
      // then handed the ENVELOPE to onSent, whose caller does
      // `if (d?.id) setQueryDataMapped(..., mapTicket(d))` — mapping the
      // envelope. unwrap() gives null instead, and onSent's `d?.id` guard
      // correctly skips the cache write. The email itself already sent, so the
      // toast is still the truth.
      const d = unwrap<any>(response.data);
      invalidateTickets(queryClient);
      onToast?.(`Email sent to ${email}`);
      onSent(d);
      setEmail('');
      setSaveToCustomer(true);
      onClose();
    },
    onError: (err: any) => {
      const refusal = suppressedRefusalOf(err);
      if (refusal) {
        // Stays in the modal, next to the field it is about. A toast here is
        // what left the office with no route out: the sentence vanished and the
        // only remaining control re-posted the same refused address.
        setSuppressed(refusal);
        invalidateTickets(queryClient);
        return;
      }
      const message =
        err?.response?.data?.error ??
        err?.message ??
        'Failed to send email. Please try again.';
      onToast?.(message, 'error');
      // The API refuses a known-bounced address with 409 AFTER moving the
      // ticket to 'bounced' (the red correction banner). The modal stays open
      // so the address can be fixed; the ticket behind it must not keep
      // reading "sent". Same reason the success path invalidates.
      invalidateTickets(queryClient);
    },
  });

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <div
        className="bg-fieldiq-card border border-fieldiq-border rounded-2xl w-full max-w-sm shadow-2xl animate-fade-in p-5"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-1">
          <h3 className="text-lg font-bold text-white">{title || 'Send Approval Email'}</h3>
          <button
            onClick={onClose}
            className="p-1 text-fieldiq-muted hover:text-white transition-colors"
          >
            <X size={18} />
          </button>
        </div>
        <p className="text-sm text-fieldiq-text mb-4">
          {description || (
            <>
              We don't have an email on file for{' '}
              <span className="font-medium text-white">{ticket.customer}</span>.
              Where should we send the approval link?
            </>
          )}
        </p>

        {suppressed && (
          <div className="mb-3">
            <SuppressedRecipientNotice
              refusal={suppressed}
              documentLabel="ticket"
              busy={sendMutation.isPending}
              onForce={() => sendMutation.mutate({ force: true })}
              onDismiss={() => setSuppressed(null)}
            />
          </div>
        )}

        <input
          type="email"
          value={email}
          onChange={(e) => { setEmail(e.target.value); setSuppressed(null); }}
          placeholder="customer@example.com"
          className="w-full bg-fieldiq-dark border border-fieldiq-border rounded-lg px-3 py-2.5 text-white text-sm focus:border-fieldiq-accent focus:outline-none mb-3"
          autoFocus
          onKeyDown={(e) => {
            if (e.key === 'Enter' && isValidEmail && !sendMutation.isPending) {
              sendMutation.mutate(undefined);
            }
          }}
        />

        <label className="flex items-center gap-2 cursor-pointer mb-4">
          <input
            type="checkbox"
            checked={saveToCustomer}
            onChange={(e) => setSaveToCustomer(e.target.checked)}
            className="w-4 h-4 rounded"
          />
          <span className="text-xs text-fieldiq-text">
            Save this email to {ticket.customer}'s customer record
          </span>
        </label>

        <div className="flex gap-3">
          <button
            onClick={onClose}
            className="flex-1 bg-fieldiq-dark border border-fieldiq-border text-white py-2.5 rounded-xl text-sm font-medium hover:border-fieldiq-muted transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={() => sendMutation.mutate(undefined)}
            disabled={!isValidEmail || sendMutation.isPending}
            className="flex-1 bg-blue-600 text-white py-2.5 rounded-xl text-sm font-semibold hover:bg-blue-500 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {sendMutation.isPending ? (
              <Loader2 size={14} className="animate-spin" />
            ) : (
              <Mail size={14} />
            )}
            {sendMutation.isPending ? 'Sending...' : 'Send Approval Email'}
          </button>
        </div>
      </div>
    </div>
  );
}
